import { useEffect, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import type { RegistrationStatus } from "@/types";
import { Loader2 } from "lucide-react";

interface RegistrationGuardProps {
  children: ReactNode;
  requiredStatus: RegistrationStatus;
}

export function RegistrationGuard({
  children,
  requiredStatus,
}: RegistrationGuardProps) {
  const { isAuthenticated, isLoading, status, checkAuth } = useAuthStore();

  useEffect(() => {
    if (!isAuthenticated) {
      checkAuth();
    }
  }, [isAuthenticated, checkAuth]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If user has completed registration, go to dashboard
  if (status === "complete") {
    return <Navigate to="/dash" replace />;
  }

  // If user is at wrong step, redirect to correct one
  if (status !== requiredStatus) {
    if (status === "needsEmailVerification") {
      return <Navigate to="/verify-email" replace />;
    }
    if (status === "needsSubscription") {
      return <Navigate to="/subscription" replace />;
    }
  }

  return <>{children}</>;
}
